// Ported from design/project/App11-Matchmaking.dc.html, then wired to the live queue.
import { useEffect, useState } from "react";
import { mmss } from "../format.js";
import { initials, searchCancelledTo, useGame } from "../game.js";
import { go, setBackOverride } from "../router.js";

/** After this long with nobody on the other side, offer faster options (screen 50). */
const SLOW_MATCH_MS = 30_000;

export function App11_Matchmaking() {
  const { me, selection, queued, queuedAt, queueDepth, leaveQueue, nudged, markNudged } = useGame();
  const [now, setNow] = useState(Date.now());
  const [cancel, setCancel] = useState<"idle" | "busy" | "failed">("idle");
  // Cancel only goes back (to the lobby, or the chart it started from) if the search really ended; if a match got there first, leaveQueue
  // has already taken the player into the battle.
  const onCancel = () => {
    setCancel("busy");
    leaveQueue()
      .then((left) => left && go(searchCancelledTo(), true))
      .catch(() => setCancel("failed"));
  };
  // Back is the same as Cancel: leaving the screen must not leave the search running out of sight.
  useEffect(() => {
    setBackOverride(onCancel);
    return () => setBackOverride(null);
  }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const waited = queuedAt ? now - queuedAt : 0;
  useEffect(() => {
    if (!queued) go("App09", true);
    else if (waited >= SLOW_MATCH_MS && !nudged) {
      markNudged();
      go("App50", true);
    }
  }, [queued, waited, nudged]);
  const other = queued === "long" ? "short" : "long";
  const arena = selection.arena === "XAU" ? "GOLD" : selection.arena;
  const waiting = queueDepth ? queueDepth.long + queueDepth.short : null;
  return (
    <>
      <div className="screen">
  <div className="orbL" aria-hidden="true" style={{ top: "100px", left: "-195px", width: "480px", height: "480px" }}></div>
  <div className="orbR" aria-hidden="true" style={{ top: "100px", right: "-195px", width: "480px", height: "480px" }}></div>
  <div className="pad squeeze" style={{ height: "100%", paddingBottom: "44px" }}>
    <div className="top">
      <span className="chip chipN"><span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#CCFF00", animation: "ddPulse 1.2s infinite" }}></span>IN QUEUE</span>
      <span className="mono muted" style={{ fontSize: "13px" }}>{mmss(waited).padStart(5, "0")}</span>
    </div>

    <div style={{ marginTop: "34px", position: "relative", height: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span aria-hidden="true" style={{ position: "absolute", width: "150px", height: "150px", borderRadius: "50%", border: "1px solid rgba(255,51,102,0.6)", animation: "ddPing 2.2s ease-out infinite" }}></span>
      <span aria-hidden="true" style={{ position: "absolute", width: "150px", height: "150px", borderRadius: "50%", border: "1px solid rgba(255,51,102,0.6)", animation: "ddPing 2.2s ease-out 1.1s infinite" }}></span>
      <svg width="300" height="300" viewBox="0 0 300 300" style={{ position: "absolute" }} aria-hidden="true">
        <circle cx="150" cy="150" r="140" fill="none" stroke="rgba(255,255,255,0.06)"></circle>
        <circle cx="150" cy="150" r="104" fill="none" stroke="rgba(255,255,255,0.08)" strokeDasharray="2 6"></circle>
        <circle cx="150" cy="150" r="68" fill="none" stroke="rgba(255,255,255,0.06)"></circle>
      </svg>
      <div style={{ position: "absolute", width: "300px", height: "300px", animation: "ddSpin 14s linear infinite" }}>
        <div className="av" style={{ position: "absolute", left: "132px", top: "-8px", width: "36px", height: "36px", background: "#8A7CFF", fontSize: "11px", opacity: "0.55" }}>SQ</div>
        <div className="av" style={{ position: "absolute", left: "252px", top: "168px", width: "32px", height: "32px", background: "#3DFFD0", fontSize: "10px", opacity: "0.45" }}>0X</div>
        <div className="av" style={{ position: "absolute", left: "30px", top: "210px", width: "34px", height: "34px", background: "#FF8A3D", fontSize: "10px", opacity: "0.5" }}>BR</div>
        <div className="av" style={{ position: "absolute", left: "40px", top: "50px", width: "28px", height: "28px", background: "#FF3366", fontSize: "9px", opacity: "0.4" }}>MW</div>
      </div>
      <div style={{ position: "relative", width: "104px", height: "104px", borderRadius: "50%", padding: "3px", background: "conic-gradient(#CCFF00, #CCFF00 40%, rgba(204,255,0,0.1) 40%)", boxShadow: "0 0 40px rgba(204,255,0,0.35)" }}>
        <div className="av" style={{ width: "98px", height: "98px", background: "#CCFF00", fontSize: "30px", border: "4px solid #0B0E14", boxSizing: "border-box" }}>{initials(me)}</div>
      </div>
    </div>

    <h1 className="h1" style={{ marginTop: "18px", textAlign: "center" }}>Finding a <span className={other === "short" ? "red" : "lime"}>{other.toUpperCase()}</span></h1>
    <p className="muted" style={{ margin: "8px 0 0", textAlign: "center", fontSize: "14px" }}>Someone has to be wrong. Usually takes under 10 seconds.</p>

    <div className="g" style={{ marginTop: "22px", borderRadius: "18px", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", padding: "14px 0" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}><span className="lbl" style={{ fontSize: "10px" }}>ASSET</span><span className="mono" style={{ fontWeight: "700", fontSize: "15px" }}>{arena}{selection.durationSec === 60 && <span className="red"> 1:00</span>}</span></div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", borderLeft: "1px solid rgba(255,255,255,0.08)", borderRight: "1px solid rgba(255,255,255,0.08)" }}><span className="lbl" style={{ fontSize: "10px" }}>YOUR CALL</span><span className={`mono ${queued === "short" ? "red" : "lime"}`} style={{ fontWeight: "700", fontSize: "15px" }}>{(queued ?? "long").toUpperCase()}</span></div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}><span className="lbl" style={{ fontSize: "10px" }}>STAKE</span><span className="mono" style={{ fontWeight: "700", fontSize: "15px" }}>{selection.stake} USDT</span></div>
    </div>

    <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: "12px" }}>
      <span className="muted" style={{ textAlign: "center", fontSize: "12px" }}>{waiting !== null ? <><span className="lime mono" style={{ fontWeight: "700" }}>{waiting}</span> {waiting === 1 ? "player" : "players"} queued on {arena} right now</> : " "}</span>
      <button className="btnS" disabled={cancel === "busy"} onClick={onCancel}>
        {cancel === "busy" ? "Cancelling..." : cancel === "failed" ? "No connection. Tap to try again" : "Cancel search"}
      </button>
    </div>
  </div>
</div>
    </>
  );
}
